import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { RegistrationModal } from "@/components/RegistrationModal";
import { Newsletter } from "@/components/sections/Newsletter";
import { NewsCard, fmtDate } from "@/components/sections/News";

function Shell({ children }) {
  const [regOpen, setRegOpen] = useState(false);
  return (
    <div className="grain relative min-h-screen bg-ink">
      <Navbar onRegister={() => setRegOpen(true)} />
      <main className="pt-28 md:pt-32">{children}</main>
      <Newsletter />
      <Footer onRegister={() => setRegOpen(true)} />
      <RegistrationModal open={regOpen} onOpenChange={setRegOpen} />
    </div>
  );
}

export default function NewsPage() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/news").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  return (
    <Shell>
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20" data-testid="news-page">
        <Link to="/" data-testid="news-back-home" className="inline-flex items-center gap-2 text-sm text-cream/60 hover:text-cream"><ArrowLeft className="w-4 h-4" /> Accueil</Link>
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-emerald-bright mt-8 mb-3">La vie du club</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight">Toutes les <span className="text-mustard">actus</span></h1>
        <p className="mt-5 max-w-2xl text-cream/70">Events, battles, stages, spectacles : retrouvez ici toutes les newsletters de l'AJDR Factory.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {items?.map((n, i) => <NewsCard key={n.id} item={n} i={i} />)}
          {items && items.length === 0 && <p className="text-cream/40 font-mono2 text-sm" data-testid="news-empty">Aucune actu publiée pour le moment.</p>}
        </div>
      </section>
    </Shell>
  );
}

export function NewsArticle() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setItem(null); setError(false);
    api.get(`/news/${slug}`).then((r) => setItem(r.data)).catch(() => setError(true));
  }, [slug]);

  return (
    <Shell>
      <article className="max-w-3xl mx-auto px-4 sm:px-8 pb-20" data-testid="news-article">
        <Link to="/actus" data-testid="news-back-list" className="inline-flex items-center gap-2 text-sm text-cream/60 hover:text-cream"><ArrowLeft className="w-4 h-4" /> Toutes les actus</Link>
        {error && <p className="mt-10 text-cream/50 font-mono2" data-testid="news-not-found">Cette actu n'existe pas ou n'est pas publiée.</p>}
        {item && (
          <>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-cream/40 mt-8">{fmtDate(item.created_at)}</p>
            <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3" data-testid="news-article-title">{item.subject}</h1>
            {item.image_url && <img src={item.image_url} alt="" className="w-full rounded-3xl mt-8 border border-white/5" />}
            <div className="mt-8 text-cream/80 leading-relaxed text-base md:text-lg whitespace-pre-line" data-testid="news-article-content">{item.content}</div>
            {item.cta_url && (
              <a href={item.cta_url} target="_blank" rel="noreferrer" data-testid="news-article-cta" className="btn-mustard rounded-full px-7 py-3.5 inline-flex items-center gap-2 mt-10">
                {item.cta_label || "En savoir plus"} <ArrowUpRight className="w-4 h-4" />
              </a>
            )}
          </>
        )}
      </article>
    </Shell>
  );
}
